import { formatText } from "../i18n/texts";
import type { AccessSummary, Interval, Member, MemberPause, PersonChatRole, Reception } from "./teamApi";

// Textos de Chat › Equipo: TeamText, TeamAccessText y TeamRoleText del portal actual. La pantalla solo traduce los códigos
// que calcula el servidor (A5: ACCESS_STATE_*, ACCESS_REASON_*, CHAT_ROLE_*); nunca arma su propia regla de acceso.

/** Lo que cada pieza de Equipo recibe: los textos, el idioma, la zona de quien mira y su usuario (OperatingUiContext). */
export type TeamUi = {
  labels: Record<string, string>;
  locale: string;
  timeZone: string;
  userId: number;
  organizationId: number;
  theme: "light" | "dark";
};

/** Prefijos y claves compartidas que carga Equipo (TeamText.Labels): lo demás se ve como clave faltante. */
export const teamPrefixes = ["CFG1_", "TEAM_CHAT_", "ACCESS_STATE_", "ACCESS_REASON_", "CHAT_ROLE_"];
export const teamSharedKeys = ["BRAND_LOADING", "TEAM_ERR_FORBIDDEN", "ROLE_AGENT", "ROLE_SUPERVISOR"];

/** Solo las claves de Equipo, como TeamText.Labels. */
export function teamLabels(dictionary: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(dictionary).filter(
      ([key]) => teamPrefixes.some((prefix) => key.startsWith(prefix)) || teamSharedKeys.includes(key),
    ),
  );
}

export const text = (ui: TeamUi, key: string): string => ui.labels[key] ?? `⟦${key}⟧`;

export const receives = "RECEIVES_AUTOMATICALLY",
  noInbox = "NO_INBOX",
  notParticipating = "NOT_PARTICIPATING",
  available = "AVAILABLE",
  paused = "PAUSED",
  noChatAccess = "NO_CHAT_ACCESS";
export const chatRoles = ["CHAT_AGENT", "CHAT_COORDINATOR", "CHAT_ATTENTION_SUPERVISOR", "CHAT_ADMIN"];

/** Singular y plural (C24) con el número del idioma. */
export function count(ui: TeamUi, value: number, one: string, many: string): string {
  // string.Format con un entero no agrupa miles: «1000 miembros».
  return formatText(text(ui, value === 1 ? one : many), value);
}
export const members = (ui: TeamUi, value: number) => count(ui, value, "CFG1_R1_MEMBERS_ONE", "CFG1_R1_MEMBERS_MANY");

/** Estado de la cuenta en la compañía (dato, no se cambia aquí). */
export function status(ui: TeamUi, value: string): string {
  return text(
    ui,
    value === "ACTIVE"
      ? "CFG1_ACTIVE"
      : value === "BLOCKED"
        ? "CFG1_BLOCKED"
        : value === "SUSPENDED"
          ? "CFG1_SUSPENDED"
          : "CFG1_INACTIVE",
  );
}

/** Rol de compañía con su nombre del negocio; los demás con el nombre que les dio la empresa. */
export function companyRole(ui: TeamUi, code: string, name: string | null | undefined): string {
  if (code === "ORG_OWNER") return text(ui, "CFG1_OWNER");
  if (code === "ORG_ADMIN") return text(ui, "CFG1_ADMINISTRATOR");
  if (code === "ORG_MEMBER") return text(ui, "CFG1_COMPANY_MEMBER");
  return name ?? code;
}

/** Nombre de un rol de Chat: los 4 de Bheli con su texto; uno personalizado con su nombre. */
export function roleName(ui: TeamUi, code: string | null | undefined, roles?: PersonChatRole[]): string {
  if (code == null) return text(ui, "CFG1_B_ROLE_NONE");
  if (chatRoles.includes(code)) return text(ui, "CHAT_ROLE_" + code);
  return roles?.find((role) => role.code === code)?.name ?? code;
}

/** Gris lo que no participa; ocre lo que deja sin recibir a quien atiende. */
export function assignmentTone(assignment: string): string {
  return assignment === receives ? "" : assignment === noInbox || assignment === notParticipating ? "meta" : "ochre";
}

/** B1 · Asignación: «Recibe en 1 de 2» cuando recibe en parte de sus bandejas; si no, el estado. */
export function assignment(ui: TeamUi, summary: AccessSummary): { text: string; tone: string } {
  if (summary.assignment === receives && summary.receivingNow > 0 && summary.receivingNow < summary.inboxes)
    return { text: formatText(text(ui, "CFG1_B_RECEIVES_IN"), summary.receivingNow, summary.inboxes), tone: "" };
  return { text: text(ui, "ACCESS_STATE_" + summary.assignment), tone: assignmentTone(summary.assignment) };
}

/** B1 · Disponibilidad: «—» donde el reparto no la considera (sin bandeja o supervisa). */
export function availability(
  ui: TeamUi,
  assignmentCode: string,
  availabilityCode: string,
  until: string | null | undefined,
): { text: string; tone: string } {
  if (assignmentCode === noInbox || assignmentCode === notParticipating) return { text: "—", tone: "meta" };
  if (availabilityCode === paused && until)
    return { text: formatText(text(ui, "CFG1_B_PAUSED_UNTIL"), localTime(ui, until, ui.timeZone)), tone: "ochre" };
  return { text: text(ui, "ACCESS_STATE_" + availabilityCode), tone: availabilityCode === available ? "" : "ochre" };
}

export const reason = (ui: TeamUi, code: string | null | undefined): string =>
  code == null ? "" : text(ui, "ACCESS_REASON_" + code);
/** El motivo dicho para varias bandejas cuando tiene esa forma; si no, el de una. */
export function reasonFor(ui: TeamUi, code: string | null | undefined, inboxes: number): string {
  return code != null && inboxes > 1 && `ACCESS_REASON_${code}_MANY` in ui.labels
    ? text(ui, `ACCESS_REASON_${code}_MANY`)
    : reason(ui, code);
}

/** «Hoy recibe automáticamente.» o «Hoy no recibe: …». */
export function todayLine(ui: TeamUi, reception: Reception): string {
  if (reception.assignment === receives && reception.availability === available)
    return text(ui, "CFG1_B_TODAY_RECEIVES");
  const why = reception.assignment === receives ? reception.availabilityReason : reception.assignmentReason;
  return formatText(text(ui, "CFG1_B_TODAY_NOT"), inSentence(ui, reason(ui, why)));
}

export function mode(ui: TeamUi, value: string): string {
  return text(
    ui,
    value === "ROUND_ROBIN"
      ? "CFG1_B_MODE_ROUND_ROBIN"
      : value === "BALANCED"
        ? "CFG1_B_MODE_BALANCED"
        : value === "DIRECT"
          ? "CFG1_B_MODE_DIRECT"
          : "CFG1_B_MODE_MANUAL",
  );
}

/** Línea 2 de la persona: «Activo» o «Activo · sin acceso a Chat». */
export function accessLine(ui: TeamUi, member: Member): string {
  return member.accessState === noChatAccess
    ? `${status(ui, member.status)} · ${text(ui, "CFG1_B_NO_CHAT_SUFFIX")}`
    : status(ui, member.status);
}

/** Bandejas de la fila: «—», el nombre de la única o «2 bandejas». */
export function inboxesText(ui: TeamUi, member: Member): string {
  const mine = (member.inboxes ?? []).filter((inbox) => inbox.role != null);
  if (mine.length === 0) return "—";
  if (mine.length === 1) return mine[0]!.name;
  return formatText(text(ui, "CFG1_B_INBOXES_MANY"), mine.length);
}

/** «Agente» o «Supervisor de bandeja». */
export const functionName = (ui: TeamUi, value: string | null | undefined) =>
  value === "SUPERVISOR" ? text(ui, "CFG1_B_INBOX_SUPERVISOR") : text(ui, "ROLE_AGENT");

/** Por qué el interruptor/pausa no se puede tocar (null si se puede). */
export function switchBlocked(ui: TeamUi, member: Member): string | null {
  if (member.status !== "ACTIVE") return text(ui, "CFG1_R1_SWITCH_INACTIVE");
  if (!member.hasAssignmentAuthority) return takeBlocked(ui, member) ?? text(ui, "CFG1_R1_SWITCH_NO_TAKE");
  if (member.pause?.groupBlocked && !member.pause.individual)
    return formatText(text(ui, "CFG1_R1_SWITCH_GROUP"), member.pause.groupNames.join(", "));
  if (member.userId === ui.userId) return text(ui, "CFG1_R1_SWITCH_SELF");
  return member.canPause ? null : text(ui, "CFG1_R1_SWITCH_NOT_MANAGEABLE");
}

function takeBlocked(ui: TeamUi, member: Member): string | null {
  const inboxes = member.inboxes ?? [];
  if (inboxes.length === 0) return text(ui, "CFG1_R1_SWITCH_NO_INBOX");
  if (!member.hasChatTake) return text(ui, "CFG1_R1_SWITCH_TAKE_OFF");
  const blocked = inboxes.filter((inbox) => !inbox.canTake);
  if (blocked.length === 0) return null;
  return formatText(text(ui, "CFG1_R1_SWITCH_AGENT_NO_TAKE"), blocked.map((inbox) => inbox.name).join(", "));
}

/** D2 · por qué no se le puede quitar del Chat (null si se puede). */
export function removalBlocked(ui: TeamUi, member: Member): string | null {
  if (member.userId === ui.userId) return text(ui, "CFG1_R1_REMOVE_SELF");
  if (member.roles.includes("ORG_OWNER")) return text(ui, "CFG1_R1_REMOVE_OWNER");
  if (member.roles.includes("ORG_ADMIN")) return text(ui, "CFG1_R1_REMOVE_ADMIN");
  if (member.status !== "ACTIVE") return text(ui, "CFG1_R1_REMOVE_INACTIVE");
  return member.canManage ? null : text(ui, "CFG1_R1_SWITCH_NOT_MANAGEABLE");
}

/** Bandejas que deja al quitarlo del Chat (las que atiende con un rol). */
export function removedInboxes(member: Member): { inboxId: number; name: string; role: string }[] {
  return (member.inboxes ?? [])
    .filter((inbox) => inbox.role != null)
    .map((inbox) => ({ inboxId: inbox.inboxId, name: inbox.name, role: inbox.role! }));
}

/** Primera letra en minúscula, para insertar una causa dentro de una frase. */
export function inSentence(ui: TeamUi, value: string): string {
  return value.length === 0 ? value : value[0]!.toLocaleLowerCase(ui.locale) + value.slice(1);
}

const pad = (value: number) => String(value).padStart(2, "0");

/** Días y horas de un horario en una línea: «Lun–Vie · 09:00–18:00 · Sáb · 09:00–13:00». La semana empieza el lunes. */
export function scheduleSummary(ui: TeamUi, intervals: Interval[]): string {
  if (intervals.length === 0) return text(ui, "CFG1_R1_NO_DAYS");
  const day = (value: number) => {
    const name = abbreviatedDay(ui.locale, value).replace(/\.+$/, "");
    return name[0]!.toLocaleUpperCase(ui.locale) + name.slice(1);
  };
  const hours = (interval: Interval) =>
    interval.startMinute === 0 && interval.endMinute === 1440
      ? text(ui, "CFG1_FULL_DAY")
      : `${pad(Math.trunc(interval.startMinute / 60))}:${pad(interval.startMinute % 60)}–${pad(Math.trunc((interval.endMinute % 1440) / 60))}:${pad((interval.endMinute % 1440) % 60)}`;
  const order = [1, 2, 3, 4, 5, 6, 0];
  const byDay = new Map<number, string>();
  for (const value of new Set(intervals.map((interval) => interval.day)))
    byDay.set(
      value,
      intervals
        .filter((interval) => interval.day === value)
        .sort((a, b) => a.startMinute - b.startMinute)
        .map(hours)
        .join(", "),
    );
  const parts: string[] = [];
  for (let index = 0; index < order.length;) {
    const current = byDay.get(order[index]!);
    if (current === undefined) {
      index++;
      continue;
    }
    let last = index;
    while (last + 1 < order.length && byDay.get(order[last + 1]!) === current) last++;
    parts.push(`${last === index ? day(order[index]!) : `${day(order[index]!)}–${day(order[last]!)}`} · ${current}`);
    index = last + 1;
  }
  return parts.join(" · ");
}

/** Nombre corto del día como .NET (AbbreviatedDayNames): es «lun.», en «Mon». 0 = domingo. */
export function abbreviatedDay(locale: string, day: number): string {
  const date = new Date(Date.UTC(2026, 9, 4 + day)); // 4/10/2026 fue domingo
  const name = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(date);
  return locale.startsWith("es") && !name.endsWith(".") ? name + "." : name;
}

type Parts = { year: number; month: number; day: number; hour: number; minute: number };

/** Fecha y hora del instante en la zona (como TimeZoneInfo.ConvertTime). */
export function partsInZone(instant: string | Date, zone: string): Parts {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone: validZone(zone),
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  });
  const get = (type: string) => Number(format.formatToParts(date).find((part) => part.type === type)?.value ?? 0);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") % 24, minute: get("minute") };
}

export function validZone(zone: string | null | undefined): string {
  if (!zone) return "UTC";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return "UTC";
  }
}

/** .NET «t»: es-PE «HH:mm», en-US «h:mm AM». */
export function shortTime(locale: string, hour: number, minute: number): string {
  if (locale.startsWith("en")) return `${hour % 12 === 0 ? 12 : hour % 12}:${pad(minute)} ${hour < 12 ? "AM" : "PM"}`;
  // es-PE en .NET (ICU): «HH:mm» con cero inicial («09:00», «00:00»), como se ve en el portal actual.
  return `${pad(hour)}:${pad(minute)}`;
}
/** .NET «d»: es-PE «dd/MM/yyyy», en-US «M/d/yyyy». */
export function shortDate(locale: string, year: number, month: number, day: number): string {
  return locale.startsWith("en") ? `${month}/${day}/${year}` : `${pad(day)}/${pad(month)}/${year}`;
}
/** .NET «g». */
export const shortDateTime = (locale: string, parts: Parts) =>
  `${shortDate(locale, parts.year, parts.month, parts.day)} ${shortTime(locale, parts.hour, parts.minute)}`;

/** Fecha y hora local: hoy solo la hora; otro día, la fecha corta y la hora (TeamText.LocalTime). */
export function localTime(ui: TeamUi, instant: string, zone: string): string {
  const local = partsInZone(instant, zone);
  const today = partsInZone(new Date(), zone);
  return local.year === today.year && local.month === today.month && local.day === today.day
    ? shortTime(ui.locale, local.hour, local.minute)
    : shortDateTime(ui.locale, local);
}

/** Etiqueta de la pausa del miembro (TeamText.PauseTag). */
export function pauseTag(ui: TeamUi, pause: MemberPause | null | undefined): string | null {
  if (!pause || (!pause.individual && !pause.groupBlocked)) return null;
  if (!pause.individual) return text(ui, "CFG1_R1_TAG_GROUP_PAUSED");
  return pause.until
    ? formatText(text(ui, "CFG1_R1_PAUSED_UNTIL"), localTime(ui, pause.until, ui.timeZone))
    : text(ui, "CFG1_PAUSED");
}

/** Resumen de una función de grupo sin descripción: lo que permite (TeamRoleText.GroupSummary). */
export function groupSummary(ui: TeamUi, permissions: string[]): string {
  const keys: Record<string, string> = {
    "chat.read.group": "CFG1_GROUP_READ",
    "chat.take": "CFG1_TAKE",
    "chat.assign": "CFG1_REASSIGN",
    "chat.reply": "CFG1_REPLY",
    "chat.attach": "CFG1_ATTACH",
    "chat.close": "CFG1_CLOSE",
  };
  const names = permissions.filter((code) => keys[code]).map((code) => text(ui, keys[code]!));
  return names.length === 0 ? text(ui, "CFG1_R1_NO_PERMISSIONS") : names.join(", ");
}

/** Nombre de una función de grupo (OperatingRole.Label). */
export function groupRoleLabel(ui: TeamUi, role: { code: string; name: string }): string {
  if (role.code === "CFG1_CHAT_OPERATOR") return text(ui, "CFG1_OPERATOR");
  if (role.code === "CFG1_CHAT_SUPERVISOR") return text(ui, "CFG1_SUPERVISOR");
  if (role.code === "CFG1_CHAT_READER") return text(ui, "CFG1_READER");
  return role.name;
}

/** Ciudad de una zona IANA (IanaTimezones.City). */
export const city = (id: string) => id.split("/").at(-1)!.replace(/_/g, " ");

/** Comparación sin mayúsculas ni tildes (CompareOptions.IgnoreCase | IgnoreNonSpace). */
export const fold = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
export const contains = (value: string, search: string) => fold(value).includes(fold(search));

/** Orden de nombres como StringComparer.CurrentCultureIgnoreCase. */
export const byName = (locale: string) => (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, locale, { sensitivity: "accent" });
